
import { Component, Vue, Watch } from 'vue-property-decorator';
import { State, namespace } from 'vuex-class';
import { ChatState } from '@/store/chat/model';

import MessageList from '@/components/LiveChat/MessageList.vue';
import LiveChatInput from '@/components/LiveChat/Input.vue';
import ChatRating from '@/views/Chatroom/ChatRating.vue';
import Header from '@/components/Header.vue';
import { emitArticleAutocompleteCollapse, emitOpenArticle, emitOpenMyRequest, ArticleInteractionBus, onFloatButton, onAppFloatButton, ReloadActiveConversationBus, emitArticleAutocompleteFocused } from '@/common/eventBus';

import { sendToParent, listenFromParent, platform, ParentEvDataVoicePermission, Bridge } from '@/common/bridge';
import FloatButton from './FloatButton.vue';
import ChatroomWrapper from './ChatroomWrapper.vue';
import RuleEngineHelper from './RuleEngineHelper/index.vue';
import BottomWrapper from './BottomWrapper.vue';
import { stepEnum } from '@/views/Chatroom/model';
import { FeedbackCategory } from '@/model/pleased';
import Voice from '@/views/Chatroom/RuleEngineHelper/Voice.vue';
import VoiceDisclaimer from '@/views/Chatroom/RuleEngineHelper/VoiceDisclaimer.vue';
import PleasedChatClient from '@/vendor/pleased-sdk';

import * as PleasedApi from '@/common/api/api.pleased';
import * as PleasedModel from '@/common/api/models.pleased';
import * as env from '@/common/environment';
import * as Locale from '@/plugins/i18n';
import { reportingService } from '@/common/reporting';

import { AiMessageForUser, ApplyMetadataAutomationExternEvent, BaseEvent, BaseMessage, CancelSupportRequestExternEvent, ConnectToAgent, GuestLoginEvent, MessageListItem, ResumeAIChatEvent, SystemListMessage, UserFileForAIEvent, UserInputSubmittedTransactionEvent, UserInputSubmittedTransactionForAiEvent, UserLoginEvent, UserMessageForAI, UserMessageForAIEvent, UserTransactionMessage } from '@/common/bot/types';
import { createTypingMessage, ItemType, spawnSystemMessage, spawnUserSystemMessage, spawnCategoryListMessage, findMessageIndex, deleteMessage, BotConstants, getActionKeyFromCategoryName, createNotice, randomId, spawnUserTransactionMessage, spawnMessage, spawnMediaMessage, canConnectToAgent, EventType, getCategoryTitleFromMessageHistory, getSectionTitleFromMessageHistory, createMessageHistory } from '@/common/bot/lib';
import { MemberType, MessageType, PredefinedOptionType, RateAiResponseKind, RateAiResponseType } from '@/common/bot/enum';
import { ConnectRequestEvent, IUpdatePleasedUser, PleasedGuestUserInfo, PleasedUserInfo, RemedyTransactionInputEvent, SelectedEntity, TransactionInputEvent, VisitorInfoFilled, VisitorInformation } from '@/common/types';
import { Country, INITIAL_LOADING_MESSAGE_ID, SYSTEM_MESSAGE_PREFIX, TransactionType } from '@/common/config';
import ConversationHeader from './active-conversations/ConversationHeader.vue';
import ActiveConversations from './active-conversations/ActiveConversations.vue';
import { Conversation } from './active-conversations/type';
import formatDuration from 'date-fns/formatDuration';
import intervalToDuration from 'date-fns/intervalToDuration';
import { autoParse } from '@/common/pleased-parser';
import ThemeButton from '@/components/ThemeButton.vue';
import ArticleAutocomplete from '@/components/ArticleAutocomplete.vue';
import ResolvedRequestBanner from '@/components/ResolvedRequestBanner.vue';
import { SystemOptionType } from '@/common/bot/enum/OptionType';
import { isVoiceChatSupported } from '@/vendor/pleased-sdk/util';
import { OpenHelpEntryType, sendEntryLog } from '@/common/entryLog';
import { MessageStoreApi, MessageUtil, getUserSelections, messageStore, getVoiceChatExtraFields, getLiveChatQueueMessages, LiveMessage } from '@/common/bot/flow-frameworks/messages';
import { ChatFramework } from '@/common/bot/flow-frameworks/ChatFramework';
import { ChatTriggerActionType, createTriggerInterceptor, MatchedChatTrigger } from '@/common/bot/flow-frameworks/chat-trigger';
import { ExternalBotEvent } from '@/common/bot/enum/ExternalBotEvent';
import { DeflectionLog } from '@/common/bot/enum/DeflectionLog';
import ConfirmResumableChat from '@/components/ConfirmResumableChat.vue';
import { isArticleOption, isCategoryOption, isSectionOption } from '@/common/bot/lib/option';
import { switcher } from '@/common/bot/flow-frameworks/switcher';
import { TransStatement, BetHistoryEntity } from '@/common/api/model';
import { TRANSACTION_TYPE_NAME_ENUM, TRANSACTION_STATUS_NAME_ENUM, isBetTransaction, BET_STATUS_NAME_ENUM, isTxStatusPending } from '@/common/transaction-utils';
import { GA_EVENT_CUSTOM_DIMENSION_ENUM, GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import { ErrorCode, parseErrorMessage } from '@/common/error';
import { getPredictionById } from '@/common/bot/flow-frameworks/ChatMessagePredictiveSuggestions.static';
import OpenRequestModal from './OpenRequestModal.vue';
import NegativeAiFeedbackModal from './modals/NegativeAiFeedbackModal.vue';
import { SetLoadingPayload } from '@/store/channel-loading.store';
import { analytics, syncLogs, CMP_CONTEXTS, COMPONENT_EVENTS, EVENT_TYPES, FEATURES_EVENTS, PAGE_EVENTS, GENERAL_EVENTS, CRITICAL_EVENTS, VOICE_EVENTS, AI_EVENTS } from '@/common/logs';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';
import { AiActionShowSelectorTypes } from '@/common/bot/flow-frameworks/ai-service/AiService.types';
import { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import { END_CHAT_FOR_RESUME_AI_SETUP, localStorageRemoveChatTriggersKeys, notEmpty, sanitizeHTML } from '@/common/utils';
import { clearSelectedTransactionsFromStorage, generateSelectedTransactionsTags, hasSelectedTransactions, SELECTED_TRANSACTIONS_STORAGE_KEY } from '@/common/selectedTransactions.utils';
import { getAiHardcodedConnectConfig } from '@/common/bot/flow-frameworks/ai-service/AiService.utils';

let plsInstance: PleasedChatClient | null = null;
const chatModule = namespace('chat');
const userModule = namespace('user');
const articleAutocompleteModule = namespace('articleAutocomplete');
const chatWarning = namespace('chatWarning');
const articleMetricsModule = namespace('articleMetrics');
const channelLoadingModule = namespace('channelLoading');

Component.registerHooks(['beforeRouteLeave']);

interface ActiveConversationState {
  total: number;
  conversations: Conversation[];
  selected?: Conversation | null;
  previousStep: stepEnum;
  backListener?: null | Function;
  clearListener?: null | VoidFunction;
  liveChatTicket?: string|number;
  route?: string;
}

function activeConversation (...states: Partial<ActiveConversationState>[]): ActiveConversationState {
  return Object.assign({
    total: 0, conversations: [], selected: null, previousStep: stepEnum.WaitRuleEngine, route: '', backListener: null,
  } as ActiveConversationState,
  ...states);
}

// Refactoring TODO: prepare plan how to split this file, since 2k lines is too much
@Component({
  components: {
    Header,
    MessageList,
    ChatroomWrapper,
    FloatButton,
    RuleEngineHelper,
    LiveChatInput,
    BottomWrapper,
    ChatRating,
    ActiveConversations,
    ConversationHeader,
    ThemeButton,
    ArticleAutocomplete,
    ConfirmResumableChat,
    OpenRequestModal,
    ResolvedRequestBanner,
    NegativeAiFeedbackModal,
  },
  directives: {
    trackClick,
  },
})
export default class Chatroom extends Vue {
  @userModule.State('token') token: any;
  @userModule.State('chatStatus') chatStatus!: string;
  @userModule.State('supportVoice') supportVoice!: boolean;
  @userModule.State('guestUser') guestUser!: PleasedGuestUserInfo;
  @userModule.State('pleasedUser') pleasedUser!: PleasedUserInfo;
  @userModule.State('metaTags') metaTags!: Record<string, string|number|boolean>;
  @userModule.State('initSelectedEntity') initSelectedEntity!: SelectedEntity | null;
  @userModule.Getter('pleasedUserName') pleasedUserName!: string;
  @userModule.Getter('pleasedUserDisplayName') pleasedUserDisplayName!: string;

  @userModule.Mutation('updateAvatarImgUrl') updateAvatarImgUrl!: Function;
  @userModule.Mutation('updatePleasedUser') updatePleasedUser!: (payload: IUpdatePleasedUser) => void;
  @userModule.Mutation('updateUserGreyListed') updateUserGreyListed!: (payload: { greyListed: boolean }) => void;

  @articleAutocompleteModule.Mutation setWidgetTitle!: (payload: string) => void;

  @channelLoadingModule.State('isLiveChatLoading') readonly isLiveChatLoading!: boolean;
  @channelLoadingModule.State('isVoiceChatLoading') readonly isVoiceChatLoading!: boolean;
  @channelLoadingModule.Mutation readonly setChannelLoading!: (payload: SetLoadingPayload) => void;

  $refs!: {
    index: HTMLElement,
    msgList: InstanceType<typeof MessageList>
  }
  stepEnum = stepEnum;
  currentStep = stepEnum['WaitRuleEngine'];
  showThresholdLimitModal: boolean = false;
  voiceChatId: null | number = null;
  didVoiceChatConnect = false;
  chatRatingType: PleasedModel.ChatRateMode = 'chat';
  // TODO: [voice dev]

  typing = false;
  isInitializingChatroom = true;

  actionableItemKey = 0; // 1 for deposits , 2 for withdrawals , 3 for bets , null otherwise

  visitorInfo: VisitorInformation = {
    displayName: '',
    email: '',
    phone: '',
  }
  showLoggedInOptions = {
    showCall: true,
    showLiveChat: true,
    showOpenRequest: false,
  }

  isGuestAllowed = false;
  isStartedConnectToAgent = false;
  forcefullyShowAutocomplete = false;

  isUserInputForAiDisabled = false;
  isUserFileUploadForAiEnabled = false;

  ticketDescription = '';
  errInfo = { // For Bottom Wrapper
    isErr: false,
    message: '',
  };

  feedbackCategories: FeedbackCategory[] = [];
  aiFeedbackCategories: FeedbackCategory[] = [];

  transactionMessage = '';
  selectedTransactions: (TransStatement | BetHistoryEntity)[] = [];
  selectedTransactionType: TransactionType = TransactionType.Deposit;

  aiShowSelectorType: ExternalTransactionKind[] | null = null;
  lastDirectRequest: PleasedModel.TicketDetail | null = null;

  activeConversations = activeConversation();
  cleanUps: Function[] = [];
  onMaximize: Function[] = [];

  botMessages: ReadonlyArray<MessageListItem> = [];
  resumeAiSessionId = null

  /**
   * This is non-reactive prop, the reactivity is handled
   * by redux, in `created()`.
   */
  $messageStore!: MessageStoreApi;
  $bot!: ChatFramework;

  @State('chat') chatState!: ChatState;

  @chatModule.State('is_chatting') isChatting: any;
  @chatModule.State('is_ready_to_chat') isReadyToChat: any;
  @chatModule.State('chats') chats!: MessageListItem[];

  @chatModule.State('queue_position') queuePosition!: number;
  @chatModule.State('agentLeave') agentLeave!: boolean;

  @chatModule.State('hasSeenRate') hasSeenRate!: boolean;
  @chatModule.State('agentJoined') agentJoined!: boolean;
  @chatModule.State('extraTransactionInfo') extraTransactionInfo!: any;
  @chatModule.Getter('subject') subject!: string;
  @chatModule.Mutation('resetChat') resetChat!: Function;

  @chatModule.Mutation('updateHasSeenRate') updateHasSeenRate!: Function;
  @chatModule.Mutation('updateInitialized') updateInitialized!: (value: boolean) => void;
  @chatModule.Action('changeTypingList') changeTypingList!: Function;
  @chatModule.Action('readyToChat') readyToChat!: Function;
  @chatModule.State('showTyping') showTyping!: boolean;
  @userModule.Getter('displayName') displayName!: string;
  @userModule.State('isLogin') isLogin!: boolean;
  @userModule.State('pageSource') pageSource!: OpenHelpEntryType;
  @userModule.State('sentEntryLog') sentEntryLog!: boolean;
  @userModule.Mutation('updateSentEntryLog') updateSentEntryLog!: Function;

  @userModule.State('country') country!: string;
  @userModule.State('userCountry') userCountry!: string;
  @userModule.State('customerAppVersion') customerAppVersion!: string | null;

  @chatWarning.Action('fetchChatWarnings') fetchChatWarnings!: () => void;

  @articleMetricsModule.Action('saveArticleMetrics') saveArticleMetrics!: (payload: { endChat?: boolean, restartChat?: boolean }) => void;

  readonly CMP_CONTEXTS = CMP_CONTEXTS;
  readonly BTN_EVTS = BTN_EVTS;

  get isAiConversation () {
    return this.currentStep === stepEnum.AiConversation || this.currentStep === stepEnum.PreAiConversation;
  }

  get isInLiveChat () {
    return this.currentStep === stepEnum['LiveChatFlow'];
  }

  get isInConversation () {
    return this.isInLiveChat || this.isAiConversation;
  }

  get showFloatButton () {
    return this.currentStep === stepEnum['ResolveEnd'] || this.currentStep === stepEnum['WaitRuleEngine'];
  }

  get isSelectedActiveConversation () {
    return !!this.activeConversations.selected;
  }

  get isActiveConversationPage () {
    return this.currentStep === stepEnum.ActiveConversation;
  }

  get selectedConversationId () {
    return this.activeConversations.selected?.id;
  }

  get messages () {
    const isActiveConversation = !!this.activeConversations.selected;

    let messages: any[] = [];
    isActiveConversation && messages.push(...this.activeConvoComments);

    if (this.botMessages.length && !isActiveConversation) {
      messages.push(...this.botMessages);
    }
    if (this.chats.length) {
      messages.push(...this.chats);
    }

    return messages;
  }
  get stickyMessages () {
    const messages = [];
    if (this.queuePosition > 0) {
      messages.push(
        createNotice({ message: this.$t('notice.queue_position', { queuePosition: this.queuePosition }) as string, id: 'queue-notice' }),
        createNotice({ message: this.$t('notice.waiting_for_agent') as string, id: 'waiting-for-agent-notice' })
      );
    }
    if (this.showTyping) {
      messages.push(BotConstants.STICKY_LOADING_MESSAGE);
    }
    return messages;
  }

  get consolidatedIssueMessage () {
    const message = this.$messageStore.get().find(m => m.id === BotConstants.IDS.USER_ISSUE_LIST) as UserTransactionMessage;
    if (message) {
      return message.issues.join(' / ');
    }
    return '';
  }

  get isAbleToSendMsg () {
    return this.isChatting && this.isReadyToChat;
  }

  get hideLanguageSelector () {
    return this.currentStep === stepEnum['LiveChatFlow'];
  }

  get userSelections () {
    return getUserSelections(this.botMessages);
  }

  get isChatroomSelected () {
    const { currentStep, activeConversations } = this;
    return Boolean(currentStep === stepEnum.WaitRuleEngine && activeConversations.total && !activeConversations.selected);
  }

  get trackEventContext () {
    return this.activeConversations.selected
      ? CMP_CONTEXTS.ACTIVE_CONVERSATION_TICKET
      : this.isActiveConversationPage
        ? CMP_CONTEXTS.ACTIVE_CONVERSATION
        : CMP_CONTEXTS.CHAT_ROOM;
  }

  get chatRatingId () {
    const chatRatingIdMap = {
      chat: plsInstance?.chatId,
      voice: this.voiceChatId,
      ticket: null,
      AI: this.resumeAiSessionId || analytics.sessionId,
    } as const;

    return chatRatingIdMap[this.chatRatingType];
  }

  get chatRatingCategories () {
    return this.chatRatingType === 'AI' ? this.aiFeedbackCategories : this.feedbackCategories;
  }

  get canConnectToAgent () {
    return canConnectToAgent(this.connectConfig);
  }

  get canConnectToAI () {
    return this.connectConfig && this.$bot.checkCanConnectToAI(this.connectConfig)
  }

  created () {
    reportingService.chatBotOpened();
    this.$router.replace('#').catch(() => {});
    document.title = 'Chatroom';

    this.$messageStore = messageStore;

    this.cleanUps.push(
      Locale.addListener(this.onLanguageChange),
      Bridge.onOpenActiveConversation(this.showConversation),
      ReloadActiveConversationBus.sub(() => this.isLogin && this.resolveActiveConversation()),
      // Setup bot
      this.$messageStore.subscribe(() => {
        const nextMessages = this.$messageStore.get();
        if (nextMessages === this.botMessages) return;
        this.botMessages = nextMessages;
      })
    );

    const onFloatButtonClick = (isOpen: boolean) => {
      if (!isOpen) {
        this.onMaximize.forEach(f => f());
        this.onMaximize = [];
      }
    };

    onFloatButton(onFloatButtonClick);
    onAppFloatButton(onFloatButtonClick);

    this.setupChatFramework();
    this.initializeChatRoomWithLoading();
  }

  beforeDestroy () {
    this.cleanUps?.forEach(c => { try { c?.(); } finally {} });
    if (plsInstance) {
      (plsInstance as any).disableTypingListener && (plsInstance as any).disableTypingListener();
    }
  }

  @Watch('isLogin')
  onUserLoggedInStatusChange () {
    this.$bot?.setOptions({
      canRunCheck: !!this.isLogin,
      enablePredictiveOptions: !!this.isLogin,
    });

    this.$bot?.setUserOptions({
      isUserLoggedIn: !!this.isLogin,
    });
  }

  // Pleased User is set fully, so we should have all fields available
  @Watch('pleasedUser.userId')
  onPleasedUserIdChange () {
    this.$bot?.setUserOptions({
      userId: this.isLogin ? this.pleasedUser.userId : '',
      userName: this.pleasedUserDisplayName,
      userLoyaltyTier: this.pleasedUser.userLoyaltyTierBand,
      userToken: this.pleasedUser.token,
    });
  }

  @Watch('guestUser.userId')
  onPleasedUserFillGuestDetails () {
    this.$bot?.setGuestOptions({
      userId: this.guestUser.userId,
      filledDetails: this.guestUser.filledDetails,
    });
  }

  setupChatFramework () {
    this.$bot = new ChatFramework({
      messages: this.$messageStore,
      interceptors: [
        createTriggerInterceptor({
          userCountry: this.userCountry,
          onMatch: this.showOverlayTrigger,
        }),
      ],
    });

    this.onUserLoggedInStatusChange();

    this.$bot.$e.on('selectOption', ({ event, selected, message }) => {
      console.log({
        event,
        selected,
        message,
      });

      analytics.trackClick({
        name: BTN_EVTS.CHAT_ROOM_MESSAGE_OPTION_CLICKED,
        metadata: {
          eventName: event.type,
          eventOption: selected,
          eventOptionType: typeof selected === 'string' ? selected : selected.kind,
          messageText: message.message,
          messageType: message.type,
          buttonText: typeof selected === 'string' ? selected : selected.title,
        },
      });

      if (isCategoryOption(selected)) {
        this.$gtag.event(GA_EVENT_NAME_ENUM.SELECT_CATEGORY, {
          [GA_EVENT_CUSTOM_DIMENSION_ENUM.CATEGORY_NAME]: selected.title,
        });
        return reportingService.selectedCategory(selected.id);
      }
      if (isSectionOption(selected)) {
        this.$gtag.event(GA_EVENT_NAME_ENUM.SELECT_SECTION, {
          [GA_EVENT_CUSTOM_DIMENSION_ENUM.SECTION_NAME]: selected.title,
        });
        return reportingService.selectedSection(selected.id);
      }
      if (isArticleOption(selected)) {
        return reportingService.selectedArticle(selected.id);
      }
    });

    this.$bot.$e.on('selectExternalOption', ({ selected }) => {
      analytics.trackClick({
        name: BTN_EVTS.CHAT_ROOM_EXTERNAL_OPTION_CLICKED,
        metadata: {
          eventOption: selected,
        },
      });
    });

    this.$bot.$e.on('openArticle', ({ articleId, sourceMessageId, title, useConfig }) => {
      emitOpenArticle({ isOpen: true, articleId, fetchArticle: true, title, sourceMessageId, useConfig });
    });

    this.$bot.$e.on('goToAction', async ({ action }) => {
      const isAppPlatform = platform === 'ios' || platform === 'app';
      await syncLogs({ endSession: !isAppPlatform });
      Bridge.goToAction(action);
    });

    this.$bot.$e.on('connect', ({ options: connect }) => {
      this.connectToAgentFlow(connect, { category: connect.category || '', sectionName: connect.section || '' });
    });

    this.$bot.$e.on('preConnectToAI', () => {
      this.currentStep = stepEnum.PreAiConversation;
    });

    this.$bot.$e.on('connectedToAI', ({ connectConfig }) => {
      this.currentStep = stepEnum.AiConversation;
      if (connectConfig) this.connectConfig = connectConfig;

      PleasedApi.getAiFeedbackSelections().then(r => {
        this.aiFeedbackCategories = r;
      });
    });

    this.$bot.$e.on('endAiConversation', () => {
      this.currentStep = stepEnum.EndChat;
      this.chatRatingType = 'AI';
    });

    this.$bot.$e.on('endChat', this.handleEndChat);

    this.$bot.$e.on('notify:restartChat', async () => {
      // we reset skipping transaction step on restart since it's the only option
      // to start flow from the beginning/select something else after connecting to agent.
      this.saveArticleMetrics({ restartChat: true });

      // Reset disable AI flow flag
      this.$bot.setAiOptions({ aiEnabledPerSession: true });

      await syncLogs({ endSession: true });
      await analytics.restartUserSession({
        sessionParams: {
          language: Locale.i18n.locale,
          platform,
          userAgent: navigator.userAgent,
        },
        metadata: {
          brandCode: env.get('brandCode'),
          language: Locale.i18n.locale,
        },
      });
    });

    this.$bot.$e.on('goToTicket', (directRequest) => {
      if (directRequest) {
        this.currentStep = stepEnum.WaitRuleEngine;
        this.showConversation({
          id: directRequest.id,
          title: directRequest.subject,
        });
      } else {
        console.error('No ticket to go to.');
      }
    });

    this.$bot.$e.on('notify:prediction:match', ({ type }) => {
      reportingService.predictionMatched(getPredictionById(type)!.selectionId);
      analytics.track({
        type: EVENT_TYPES.FEATURE,
        name: FEATURES_EVENTS.PREDICTIVE_OPTIONS_MATCHED,
        selection: type,
        // ANALYTICS TODO: check with team if need message here in metadata (requested in sheet)
      });
    });

    this.$bot.$e.on('notify:prediction:select', ({ selected }) => {
      reportingService.predictionSelection(selected === PredefinedOptionType.CONNECT_TO_SUPPORT ? 'yes' : 'no');
    });

    this.$bot.$e.on('notify:prediction:go:article', ({ articleId }) => {
      reportingService.predictionArticle(articleId);
    });

    this.$bot.$e.on('openAutocomplete', () => {
      this.forcefullyShowAutocomplete = true;
      setTimeout(() => {
        emitArticleAutocompleteCollapse(false);
        emitArticleAutocompleteFocused(true);
        // if connect to agent is started, we need to wait some time
        // so that the autocomplete can be shown properly.
      }, this.isStartedConnectToAgent ? 300 : 0);
    });

    this.$bot.$e.on('metadata:track', (metadataTrackLog: Partial<PleasedModel.MetadataLog & { metadataAutomationMessage: string }>) => {
      const { metadataAutomationMessage, ...log } = metadataTrackLog;

      log.logTraceId && PleasedApi.trackMetadataAutomation(log).catch((e) => {
        console.error('Error tracking metadata automation', e);
      });

      if (log.messageShown) {
        analytics.track({
          type: EVENT_TYPES.FEATURE,
          name: FEATURES_EVENTS.METADATA_AUTOMATION_MATCHED,
          selection: log.metadataAutomationId,
          metadata: {
            message: metadataAutomationMessage,
          },
        });
      }
    });

    this.$bot.$e.on('rateAiResponse', (event: { message: AiMessageForUser, rate: RateAiResponseKind }) => {
      const { message, rate } = event;

      if (rate === RateAiResponseType.LIKE) {
        PleasedApi.rateChatAiResponse({
          sessionId: message.sessionId,
          messageId: message.id!,
          isThumbUp: PleasedModel.ThumbRate.UP,
        });
      } else {
        this.showNegativeAiFeedbackDialog(message);
      }
    });

    this.$bot.$e.on('disableUserInputForAi', (isDisabled: boolean) => {
      this.isUserInputForAiDisabled = isDisabled;
    });

    this.$bot.$e.on('enableUserFileUploadForAi', (isEnabled: boolean) => {
      this.isUserFileUploadForAiEnabled = isEnabled;
    });

    this.$bot.$e.on('setSelectorTypes', (selectorTypes: AiActionShowSelectorTypes) => {
      console.log('setSelectorTypes', {
        selectorTypes,
      });

      const types = Array.isArray(selectorTypes.status) ? selectorTypes.status : [selectorTypes.status];
      this.aiShowSelectorType = types;
    });

    this.$bot.$e.on('showTransactionSelector', (selectorTypes: AiActionShowSelectorTypes) => {
      this.currentStep = stepEnum.RuleEngine;
      const types = Array.isArray(selectorTypes.status) ? selectorTypes.status : [selectorTypes.status];
      this.aiShowSelectorType = types;
    });

    this.$bot.$e.on('interveneAiChat', this.onInterveneAiChat);

    const removeListener = ArticleInteractionBus.sub(async (event) => {
      emitArticleAutocompleteCollapse(true);

      // if the search is from ActiveConversation page, we can ignore it.
      if (this.isActiveConversationPage) {
        if (event.payload.interaction === 'back') return;
        // We're not starting chatbot here
        if (event.payload.interaction === 'support-request') {
          const isConnectToAI = event.payload.connectToAI;

          // If AI conversation is used, then do not show categories messages.
          if (isConnectToAI) {
            this.removeInitialLoadingMessage();
          } else {
            this.startChatBot();
          }
        }
      }

      if (event.payload.interaction === 'execute-action') {
        const action = event.payload.article.action?.action;
        const isAppPlatform = platform === 'ios' || platform === 'app';
        await syncLogs({ endSession: !isAppPlatform });
        action && Bridge.goToAction(action);
        return;
      }

      if (event.payload.interaction === 'go-to-my-requests') {
        this.goToMyRequests();
      }

      this.$bot.submitExternalEvent(event);
    });

    this.cleanUps.push(
      removeListener,
      () => this.$messageStore.clear(),
    );
  }

  onLanguageChange () {
    if (this.currentStep === stepEnum['WaitRuleEngine'] || this.currentStep === stepEnum['ActiveConversation']) {
      // Reset chat.
      this.$messageStore.clear();
      this.initializeChatRoom();
    } else if (this.currentStep === stepEnum['RuleEngine']) {
      this.$messageStore.add(createNotice({ message: this.t('notice.language_change', { locale: Locale.getCurrentLanguageName() }, 'Language changed to {locale}',) }));
    }
  }

  async setupPleasedChatTokens (client: PleasedChatClient) {
    return client.init({
      access_token: this.pleasedUser.token,
      pleased_url: env.get('url'),
      brand_subdomain: env.get('brandCode'),
    });
  }

  setupPleasedChatEvents (client: PleasedChatClient) {
    const events = [
      'account_status',
      'connection_update',
      'department_update',
      'visitor_update',
      'agent_update',
      'chat',
      'error',
    ];

    events.forEach((evt) => {
      client?.on(evt, (data: any) => {
        if (!client) return;
        this.$store.dispatch({
          type: `chat/${evt}`,
          detail: data,
        });
      });
    });

    client?.on('onTypingListChange', (data:{ isTyping: boolean; displayName: string; isAgent: boolean }) => {
      if (!client) return;
      this.changeTypingList(data);
    });
    client?.on('ready_to_chat', () => {
      if (!client) return;
      this.readyToChat(true);
    });
  }

  async postInit ({ isVoice = false, resumableChat, canBeResumed }: {isVoice?: boolean; resumableChat?: PleasedModel.ActiveOpenChat|null | null; canBeResumed?: PleasedModel.VerifyChatOpenDto | null} = {}) {
    await this.setupPleasedChatTokens(plsInstance as PleasedChatClient);

    const selectedTransactions = this.selectedTransactions.map(t => {
      if (isBetTransaction(t, this.selectedTransactionType)) {
        return {
          type: TRANSACTION_TYPE_NAME_ENUM[this.selectedTransactionType],
          status: BET_STATUS_NAME_ENUM[t.winningStatus],
          txId: t.orderId,
        };
      } else {
        return {
          type: TRANSACTION_TYPE_NAME_ENUM[this.selectedTransactionType],
          status: TRANSACTION_STATUS_NAME_ENUM[t.status],
          txId: t.payChTxId,
        };
      }
    });

    PleasedApi.getFeedbackSelections().then(r => {
      this.feedbackCategories = r;
    });

    const hadAiConversation = this.connectConfig?.aiConversation;
    const { isFirstRequest, overlongConversation, selectedTransactionsData } = this.connectConfig || {};

    const selectedTransactionTags = selectedTransactionsData && hasSelectedTransactions(selectedTransactionsData)
      ? generateSelectedTransactionsTags(selectedTransactionsData)
      : [];
    const autoAddTags = [
      this.connectConfig?.appliedMetadataAutomation?.tag,
      isFirstRequest ? 'ai::first_api_error' : isFirstRequest === false ? 'ai::api_error' : null,
      overlongConversation ? 'ai::exceed_msg_limit' : null,
      // TODO: Uncomment this when we have a way to handle selected transactions in live chat
      //...selectedTransactionTags,
    ].filter(notEmpty);

    const visitorInfo = {
      display_name: this.pleasedUserName || (this.displayName ? this.displayName : this.visitorInfo.displayName),
      foreign_id: `${this.pleasedUser.foreignId}`,
      phone: this.pleasedUser.phone || this.visitorInfo.phone || '',
      email: this.visitorInfo.email || this.pleasedUser.email,
      customerAppVersion: this.customerAppVersion || '',
      ticketId: this.activeConversations.selected?.id,
      chatTags: Object.entries({
        ...this.metaTags,
        language: this.$i18n.locale, // override the language tag.
      }).filter(([, v]) => (v || '').trim()).map(([k, v]) => `${k}=${v.trim()}`),
      autoAddTags,
      subject: this.ticketDescription || this.ticketSubject,
      selectedTransactions,
      isGuest: !this.isLogin,
      // AI flow is only available from new conversation
      ...(hadAiConversation && !this.isSelectedActiveConversation ? { aiSessionId: this.connectConfig?.resumeAiSessionId || analytics.sessionId } : {}),
    };

    if (!isVoice) {
      // plsInstance is created implicitly in function executed before current one
      this.setupPleasedChatEvents(plsInstance!);
      // Resume is from main screen, no need for initial message – user will be in conversation directly
      this.removeInitialLoadingMessage();
      resumableChat && plsInstance?.resumeChat(resumableChat, visitorInfo, this.$t('resumable_chat.user_message') as string, canBeResumed);
    }

    if (!resumableChat) {
      try {
        await plsInstance?.setVisitorInfo(visitorInfo, isVoice);
        // @ts-ignore
      } catch (err: any) {
        console.error('[setVisitorInfo]: ', { err: JSON.stringify(err), isThreshold: err?.code === ErrorCode.THRESHOLD_LIMIT });
        analytics.trackCriticalEvent({
          name: CRITICAL_EVENTS.CONNECT_OPTIONS_CHAT_INITIATED_ERROR,
          metadata: {
            visitorInfo,
            error: parseErrorMessage(err),
          },
        });
        throw err;
      }
    }
  }

  /**
   * Event after the user has selected transaction/s (issue/s).
   * @event RuleEngineHelper#transaction:input
   */
  onTransactionInput ($event: TransactionInputEvent) {
    const { type, none, issues, selectedTransIssue, connectConfig } = $event;
    const message = none ? {
      [TransactionType.Deposit]: this.t('chat.user.issue.different.deposit'),
      [TransactionType.Withdrawal]: this.t('chat.user.issue.different.withdraw'),
      [TransactionType.Bet]: this.t('chat.user.issue.different.bet'),
    }[type] : issues.length ? this.t('chat.user.issue.part') : this.t('chat.user.issue.no_records');

    this.selectedTransactions = selectedTransIssue;
    this.selectedTransactionType = type;

    if (connectConfig) {
      this.connectConfig = connectConfig;
    }

    MessageUtil.addIfNotExists(
      this.$messageStore,
      spawnUserTransactionMessage({
        issues,
        message,
        id: BotConstants.IDS.USER_ISSUE_LIST,
      })
      // spawnSystemMessage({
      //   type: MessageType.DEFAULT,
      //   message: this.t('chat.sys.issue.ask_detail'),
      //   id: BotConstants.IDS.ISSUE_SYSTEM_DESCRIBE_ISSUE,
      // }),
    );
  }

  onRemedyTransactionInput ($event: RemedyTransactionInputEvent) {
    const { updatedTransactions = {} } = $event;

    const messageTitle = this.t('page.issue.transaction.remedy.status_updated_title');
    const messageContent = Object.entries(updatedTransactions).map(([tradeId, status]) => {
      const isStatusUpdated = !isTxStatusPending(status);

      return isStatusUpdated
        ? this.t('page.issue.transaction.remedy.status_updated', { tradeId, status: TRANSACTION_STATUS_NAME_ENUM[status] })
        : this.t('page.issue.transaction.remedy.status_not_updated', { tradeId });
    }).join(this.t('page.issue.transaction.remedy.and'));

    const message = `${messageTitle} ${messageContent}.`;

    this.$messageStore.add(spawnUserSystemMessage({
      type: MessageType.DEFAULT,
      message,
      id: BotConstants.IDS.REMEDY_STATUS_UPDATE,
    }));
  }

  onDirectRequestCreation (ticket: PleasedModel.TicketDetail) {
    this.lastDirectRequest = ticket;
    this.onOpenRequest(ticket.id);
  }

  onOpenRequest (requestId: number | string = '') {
    const messages = [
      spawnUserSystemMessage({
        message: this.t('chat.user.response.action', { action: this.t('global.open_request') }),
        id: BotConstants.IDS.USER_OPEN_TICKET,
        sendToChat: false,
      }),
      spawnSystemMessage({
        type: MessageType.DEFAULT,
        message: this.t('page.connect.open_request.created', { requestId }),
        id: BotConstants.IDS.SYSTEM_REQUEST_SUCCESS,
      }),
      spawnSystemMessage({
        type: MessageType.DEFAULT,
        message: this.t('page.connect.open_request.end_message'),
        id: BotConstants.IDS.SYSTEM_REQUEST_RESPONSE,
      }),
    ];
      // This is a special type. The ticket is already created by the
      // inner components, so only messages needs to be appended here.
    this.$messageStore.add(...messages);
  }

  /**
   * Event when the user requests to connect to the agent.
   * @event RuleEngineHelper#connect:request
   */
  onConnectRequest ($event: ConnectRequestEvent) {
    const { type, payload } = $event;

    this.$gtag.event(GA_EVENT_NAME_ENUM.CONTACT_SUPPORT_REQUEST, {
      [GA_EVENT_CUSTOM_DIMENSION_ENUM.CONTACT_SUPPORT_TYPE]: type,
    });

    // Key is type from ConnectRequestEvent, value track log key
    const trackEventMap = {
      openRequest: null,
      chat: COMPONENT_EVENTS.CONNECT_OPTIONS_CHAT,
      voice: COMPONENT_EVENTS.CONNECT_OPTIONS_CALL,
      addComment: COMPONENT_EVENTS.CONNECT_OPTIONS_FOLLOW_UP,
    };

    if (trackEventMap[type]) {
      const trackLogContext = payload.isFromActiveConversation
        ? CMP_CONTEXTS.ACTIVE_CONVERSATION
        : CMP_CONTEXTS.CONNECT_REQUEST;

      analytics.trackClick({
        name: `${trackLogContext}${trackEventMap[type]}`,
        ...(payload.isFromActiveConversation ? { selection: this.activeConversations.selected?.id } : {}),
      });
    }

    if (type === 'openRequest') {
      this.onOpenRequest(payload.ticketId);
    } else if (type === 'chat') {
      // Start the live chat flow.
      this.startLiveChatWithLoading({ description: payload.description || '' });
    } else if (type === 'voice') {
      // Here, no messages are required, directly start the voice chat flow.
      this.createVoiceChatWithLoading({ description: payload.description || '', deflectionLog: payload.deflectionLog });
    } else if (type === 'addComment') {
      // Show add comment for active conversation.
      this.currentStep = stepEnum.AddComment;
    }
  }

  /**
   * @event RuleEngineHelper#login:successful
   */
  async onLoginSuccessful () {
    // Add a message from the system regarding the login details.
    const message = spawnSystemMessage({
      type: MessageType.DEFAULT,
      id: BotConstants.IDS.LOGGED_IN_BY,
      message: this.$t('chat.sys.login.success', { name: this.displayName }) as string,
    });
    this.$messageStore.addSafe(message);

    await this.updateActiveConversationList();

    if (this.canConnectToAI) {
      // If user is connected with AI, we'd like to save guest conversation after login
      analytics.track({
        name: GENERAL_EVENTS.GUEST_LOGIN_AI_SUGGESTED,
      });

      if (this.connectConfig!.aiConversation && this.guestUser.token) {
        await PleasedApi.saveGuestLoginToken({
          sessionId: analytics.sessionId!,
          userToken: this.pleasedUser.token,
          guestToken: this.guestUser.token,
        }).catch((err) => {
          analytics.track({
            name: GENERAL_EVENTS.SAVE_GUEST_LOGIN_TOKEN_FAILED,
            metadata: {
              error: parseErrorMessage(err),
            },
          });
        });
      }

      const event: UserLoginEvent = {
        type: ExternalBotEvent.USER_LOGIN,
        id: randomId(),
        payload: {
          supportOptions: this.connectConfig!,
        },
      };

      this.$bot.submitExternalEvent(event);
      return;
    }

    if (!this.connectConfig?.articleId) {
      return;
    }

    // Load automations after login, in current SB implementation refresh immediately.
    PleasedApi.getMetadataAutomations({ articleId: this.connectConfig?.articleId }).then(response => {
      if (this.connectConfig) this.connectConfig.metadataRules = response.data || [];
    }).catch((e) => {
      console.error('Error fetching metadata rules', e);
    });
  }

  async onGuestLoginSuccessful (visitorInfo: VisitorInfoFilled) {
    analytics.track({
      name: GENERAL_EVENTS.GUEST_FILL_DETAILS,
      metadata: {
        visitorInfo,
      },
    });

    this.handleVisitorInfoFilled(visitorInfo);
    // We need to notify service as soon as possible about update, since it will use this data during event below
    this.$bot.setGuestOptions({ userId: visitorInfo.userData?.id, filledDetails: true });

    if (this.canConnectToAI) {
      analytics.track({
        name: GENERAL_EVENTS.SAVE_GUEST_INFO,
        metadata: {
          visitorInfo,
        },
      });
      
      await PleasedApi.saveGuestUserInfo({
        email: visitorInfo.visitorInfo.email,
        phone: visitorInfo.visitorInfo.phone,
        name: visitorInfo.visitorInfo.name,
      }).catch((err) => {
        analytics.track({
          name: GENERAL_EVENTS.SAVE_GUEST_INFO_FAILED,
          metadata: {
            error: parseErrorMessage(err),
          },
        });
      });

      const event: GuestLoginEvent = {
        type: ExternalBotEvent.GUEST_LOGIN,
        id: randomId(),
        payload: {
          supportOptions: this.connectConfig!,
        },
      };

      this.$bot.submitExternalEvent(event);
    }
  }

  onLoggedInFormFilled (visitorInfo: VisitorInfoFilled) {
    this.handleVisitorInfoFilled(visitorInfo);
  }

  handleVisitorInfoFilled ($event: VisitorInfoFilled) {
    const { visitorInfo, description } = $event;
    this.setVisitorInfo(visitorInfo);
    // Add messages describing the information from the user.
    // email, phone, name, description.
    let { displayName, email, phone = '', name = '' } = visitorInfo;
    name = name || displayName;

    email && this.$messageStore.addSafe(spawnUserSystemMessage({
      message: this.t('chat.user.info.email', [email]),
      id: BotConstants.IDS.USER_EMAIL,
    }));

    phone && this.$messageStore.addSafe(spawnUserSystemMessage({
      message: this.t('chat.user.info.phone', [phone]),
      id: BotConstants.IDS.USER_PHONE,
    }));

    name && this.$messageStore.addSafe(spawnUserSystemMessage({
      message: this.t('chat.user.info.name', [name]),
      id: BotConstants.IDS.USER_DISPLAY_NAME,
    }));

    description && this.$messageStore.addSafe(spawnUserSystemMessage({
      message: this.t('chat.user.info.issue_description', [description]),
      id: BotConstants.IDS.USER_DESCRIPTION,
      noSystemPrefix: true,
    }));

    // Do now show "Connect options" message as AI flow will proceed
    if (this.canConnectToAI) return;

    this.$messageStore.addSafe(spawnSystemMessage({
      type: MessageType.DEFAULT,
      message: this.t('chat.sys.message.connect'),
      id: BotConstants.IDS.HOW_TO_GET_IN_TOUCH,
    }));
  }

  onMetadataRuleApplied (event: ApplyMetadataAutomationExternEvent) {
    const { payload: { metadataAutomationMessage, supportOptions } } = event;

    this.$bot.submitExternalEvent(event);

    const canConnect = canConnectToAgent(supportOptions);

    if (!metadataAutomationMessage && canConnect) {
      // if no rule found and can connect, then continue default flow
      return;
    }

    this.currentStep = stepEnum.WaitRuleEngine;
  }

  async onInterveneAiChat (chatId: number) {
    // Init SDK like in new chat creation
    plsInstance = new PleasedChatClient();
    await this.setupPleasedChatTokens(plsInstance);
    this.setupPleasedChatEvents(plsInstance);

    // Fetch chat created during intervention
    const chatData = await PleasedApi.getChatById(chatId);
    this.currentStep = stepEnum['LiveChatFlow'];
    this.chatRatingType = 'chat';

    // Setup chat with the fetched data
    plsInstance.setupFirebaseChat({ data: { ...chatData, chatId } });
    this.sendMessagesToAgentChat();
  }

  // new props
  stickyMessageList: MessageListItem[] = [];
  /**
   * List of messages to show on active conversation.
   */
  activeConvoComments: MessageListItem[] = [];
  brandConfig: PleasedModel.BrandConfig | Record<string, any> = {};
  brandPredictiveOptions: PleasedModel.PredictiveSuggestionConfig[] | null = null;
  allCategories: PleasedModel.BrandCategory[] = [];
  ticketSubject = '';
  connectConfig: ConnectToAgent | null = null;
  connectCategory = '';
  /**
   * A message for the initial list of categories. This is kept in memory to use with the
   * component `<FloatButton />`.
   */
  categoryMessage: SystemListMessage | null = null;
  atomicInitializeChatRoomCall: Function | null = null;

  initializeChatRoomWithLoading () {
    this.isInitializingChatroom = true;

    this.initializeChatRoom().finally(() => {
      this.isInitializingChatroom = false;
    });
  }

  // new methods
  async initializeChatRoom () {
    console.log('Initializing chat room');
    this.atomicInitializeChatRoomCall?.();
    let ignore = false;
    this.atomicInitializeChatRoomCall = () => { ignore = true; };
    this.$messageStore.add(createTypingMessage(INITIAL_LOADING_MESSAGE_ID));

    const categoryMessageFuture = PleasedApi.getBrandConfig({ brandCode: env.get('brandCode') }).then(response => {
      this.setWidgetTitle(response.data.brandConfig.widgetTitle);

      if (!ignore) {
        this.brandConfig = response.data.brandConfig;
        this.brandPredictiveOptions = response.data.predictiveSuggestions;
        this.allCategories = response.data.categories;
      }
      return spawnCategoryListMessage({
        message: response.data.brandConfig.showWelcomeMessage ? response.data.brandConfig.welcomeMessage : '',
        options: response.data.categories.map(c => ({ ...c, kind: SystemOptionType.CATEGORY })),
        id: 'category-base',
      });
    });
    const userConfigFuture = this.token ? PleasedApi.getPleasedTokenFromSportyToken({ countryCode: this.country, sportyToken: this.token, userCountry: this.userCountry }) : Promise.resolve(null);

    const categoryFuture = categoryMessageFuture;

    const [categoryMessage, userConfig] = await Promise.all([categoryFuture, userConfigFuture]);

    if (ignore) return;

    categoryMessage && (this.categoryMessage = categoryMessage);

    const {
      welcomeMessage,
      openAiDown,
      aiEnabled,
      aiEndUserComeBackTime,
      aiWelcomeMessage,
      aiAbTestingEnabled,
      abTestPercentage: aiAbTestPercentage,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      aiBetEnabled,
      aiBetAbRatio,
      timedAiFeedbackConfig,
      aiGuestUsersAbRatio,
      aiGuestUsersEnabled,
      aiGuestUsersMessage,
      aiGuestUsersResponseText,
    } = this.brandConfig;

    this.$bot.setPreConfig({ welcomeMessage, categories: this.allCategories, predictiveConfig: this.brandPredictiveOptions });

    const {
      enableTimedAiFeedback = false,
      timedAiFeedbackNoMessagesTimeInSeconds = 0,
      timedAiFeedbackResumeMessageText = '',
      timedAiFeedbackConfirmationMessageText = '',
    } = timedAiFeedbackConfig || {};

    this.$bot.setAiOptions({
      // May be null
      openAiDown: !!openAiDown,
      aiEnabledPerBrand: !!aiEnabled,
      aiAbTestingEnabled: !!aiAbTestingEnabled,
      aiTransactionEnabled: !!aiTransactionEnabled,
      aiBetEnabled: !!aiBetEnabled,
      aiGuestUsersEnabled: !!aiGuestUsersEnabled,

      aiEndUserComeBackTime,
      aiWelcomeMessage,
      aiAbTestPercentage,
      aiTransactionAbRatio,
      aiBetAbRatio,
      enableTimedAiFeedback,
      timedAiFeedbackNoMessagesTimeInSeconds,
      timedAiFeedbackResumeMessageText,
      timedAiFeedbackConfirmationMessageText,
      aiGuestUsersAbRatio,
      aiGuestUsersMessage,
      aiGuestUsersResponseText,
    });

    const isSelectedEntityFlow = this.initSelectedEntity && this.isLogin;

    // Disable predictive options for selected entity flow
    if (isSelectedEntityFlow) {
      this.$bot.setOptions({
        enablePredictiveOptions: false,
      })
    }

    if (userConfig) {
      this.setupUserConfig(userConfig);
      await Promise.all([this.checkOpenChats(), this.resolveActiveConversation()]);

      this.fetchChatWarnings();
      // The below func is used for resuming AI chats only, the chats are sent by the API for a max of 2 minutes
      // Lets say you create a new AI conversation and then refresh the page, you will be taken back to the conversation
      // depending on the response from the API
      await this.handleRecentAIConversations();
    } else {
      this.startChatBot();
    }

    const isResumeAiFlow = !!this.resumeAiSessionId;

    // For resume AI flow we already have conversation, so we don't start Selected Entity Flow
    // If user is logged in and Initial Options have some entity
    if (!isResumeAiFlow && isSelectedEntityFlow) {
      this.startChatBot();

      analytics.track({
        name: GENERAL_EVENTS.SELECTED_ENTITY_FLOW,
        selection: this.initSelectedEntity?.type,
        metadata: {
          id: isBetTransaction(this.initSelectedEntity!.entity, this.initSelectedEntity!.type) 
            ? this.initSelectedEntity?.entity.shortId 
            : this.initSelectedEntity?.entity.tradeId,
        },
      });
      
      // TODO: We need to have a proper solution to assign auto-category for selected entity flow
      // Currently, we'll hardcode category id per type of selected entity
      if (this.initSelectedEntity?.type) {
        const categoryId = env.get('selectedEntityCategory')?.[this.initSelectedEntity?.type]?.[this.country as Country];
        categoryId && reportingService.selectedCategory(categoryId);
      }

      // send event to the bot – start selected entity flow
      // it will create message from ai side saying please select transaction with fallback config
      this.$bot.submitExternalEvent({
        type: ExternalBotEvent.START_SELECTED_ENTITY_FLOW,
        id: randomId(),
        payload: {
          selectedEntity: this.initSelectedEntity,
          supportOptions: getAiHardcodedConnectConfig()
        },
      });
    }

    if (!this.sentEntryLog) {
      // retry once on error
      sendEntryLog(this.pageSource).catch((e) => {
        console.error('Failed to send entry log', e);
        sendEntryLog(this.pageSource);
      });
      this.updateSentEntryLog(true);
    }

    // In case of repeatable chatbot open, it won't be initialized, so we need to save that it was done already
    // and redirect to the last event page on chatbot "open" event.
    this.updateInitialized(true);

    // After initialization we might have to redirect user to the last event page.
    Bridge.redirectToLastEventPage();
  }

  async handleRecentAIConversations () {
    let resumeAIConversationResponse = null;

    try {
      resumeAIConversationResponse = await this.responseForRecentAiConversations();
    } catch {
      resumeAIConversationResponse = null;
    }

    const valueFromLS = localStorage.getItem(END_CHAT_FOR_RESUME_AI_SETUP);
    const getIsAISessionAlreadyEnded = valueFromLS ? JSON.parse(valueFromLS) : null;
    const { active, sessionId } = resumeAIConversationResponse || { active: false, sessionId: null };
    this.resumeAiSessionId = sessionId;
    if (resumeAIConversationResponse && active && (!getIsAISessionAlreadyEnded || (!getIsAISessionAlreadyEnded?.includes(sessionId)))) {
      const event = {
        type: ExternalBotEvent.RESUME_AI_CHAT,
        id: randomId(),
        payload: {
          ...resumeAIConversationResponse,
        },
      } as ResumeAIChatEvent;
      this.$bot.submitExternalEvent(event);
      this.removeInitialLoadingMessage();
    } else {
      if (!active) {
        localStorage.removeItem(END_CHAT_FOR_RESUME_AI_SETUP);
      }
      clearSelectedTransactionsFromStorage();
      // We don't need resumeAiSessionId to have any value if we don't resume the AI conversation
      this.resumeAiSessionId = null;
    }
  }

  responseForRecentAiConversations = async () => {
    const aiRecentConversation = await PleasedApi.getAiRecentConversation();
    return aiRecentConversation;
  }

  checkOpenChats = async () => {
    const openChats = await PleasedApi.getOpenChats().catch(() => {
      analytics.track({ name: PAGE_EVENTS.MODAL_CONFIRM_RESUMABLE_CHAT_API_FAILED });
      return [];
    });
    const openChat = openChats[0];
    let dialog: {
      close(): void;
      loading(val: boolean): void;
    };

    if (!openChat) {
      analytics.track({ name: PAGE_EVENTS.MODAL_CONFIRM_RESUMABLE_CHAT_API_NO_DATA_RECEIVED });
      return;
    };

    const resumeChat = (resumableChat: PleasedModel.ActiveOpenChat, canBeResumed: PleasedModel.VerifyChatOpenDto) => {
      PleasedApi.checkChatCanBeResumed({ chatId: resumableChat.id });
      this.startLiveChatWithLoading({ description: resumableChat.subject, resumableChat, canBeResumed });
      dialog?.close();
    };

    const endResumableChat = (resumableChat: PleasedModel.ActiveOpenChat) => {
      dialog?.loading(true);
      localStorageRemoveChatTriggersKeys();

      PleasedApi.reportChatEnded.once({ chatId: resumableChat.id }).finally(() => {
        dialog?.loading(false);
        dialog?.close();
      });
    };

    const goToActiveConversation = async (resumableChat: PleasedModel.ActiveOpenChat) => {
      const hasActiveConversation = this.activeConversations.conversations.find(ac => ac.id === resumableChat.ticketId);
      if (!hasActiveConversation) {
        dialog?.loading(true);
        // There might be a case that there were no active conversations, and we see new active conversations
        await this.resolveActiveConversation().catch(() => {}).finally(() => {
          dialog?.loading(false);
        });
      }
      const conversation = this.activeConversations.conversations.find(ac => ac.id === resumableChat.ticketId);
      conversation && this.showConversation(conversation);
      dialog?.close();
    };

    const goToMyRequests = () => {
      emitOpenMyRequest(true);
      dialog?.close();
    };

    const closeDialog = () => dialog?.close();

    dialog = this.$dialog({
      componentBackground: 'transparent',
      content: <ConfirmResumableChat
        resumableChat={openChat}
        onResume={resumeChat}
        onCancel={endResumableChat}
        onActiveConversation={goToActiveConversation}
        onMyRequests={goToMyRequests}
        onNewChat={closeDialog}
      />,
    });
  }

  setupUserConfig (config?: PleasedModel.UserInformation, isGuest = false) {
    if (!config) return;
    console.log('setupUserConfig config', config);
    this.updateUserGreyListed({ greyListed: config.greyListed });
    console.log('Set greylisted', { greyListed: config.greyListed });
    this.updatePleasedUser({ isGuest, ...config });
  }

  endMessageFlow (positive: boolean) {
    // TODO: End the chat directly.
    this.$messageStore.addSafe(spawnSystemMessage({ message: this.$t('chat.sys.goodbye') as string, type: MessageType.DEFAULT }));
    this.currentStep = stepEnum['ResolveEnd'];
  }

  /**
   * When we want to start the connect to agent flow.
   */
  connectToAgentFlow (config: ConnectToAgent, { category, sectionName = '' }: {category: string, sectionName?: string}) {
    this.isStartedConnectToAgent = true;

    // Preserve selectedTransactionsData if they exist in the current connectConfig
    const currentSelectedTransactionsData = this.connectConfig?.selectedTransactionsData;
    this.connectConfig = {
      ...config,
      ...(currentSelectedTransactionsData && { selectedTransactionsData: currentSelectedTransactionsData }),
    };
    this.connectCategory = category;

    this.showLoggedInOptions = {
      // No call if the device does not support it.
      // showCall: config.voiceChat && this.supportVoice && PleasedChatClient.isVoiceChatSupported,
      // Instead of adding support for each individual iOS version, we will directly rely on underlying
      // detection system, checking for window.RTCPeerConnection & getUserMedia.
      // TODO: Check with enabling this patch first and then switch to individual version of
      // OS, we can see which versions this method supports and just enable their support.
      showCall: config.voiceChat && isVoiceChatSupported(platform),
      showLiveChat: config.liveChat,
      showOpenRequest: !!config.openRequest,
    };
    const transactionType = getActionKeyFromCategoryName(category);
    // A login is explicitly required, iif a transaction list needs to be shown.
    const loginRequired = config.loginRequired; // || transactionType > 0;
    // This key is for transactions
    this.actionableItemKey = transactionType;
    this.isGuestAllowed = !loginRequired;

    this.ticketSubject = (category + (sectionName.trim().length ? `+ ${sectionName}` : '')) || 'Support Request:';

    // If there is a transaction message to show.
    this.transactionMessage = config.transactionMessage || '';

    const isFilledGuestEnough = !loginRequired && this.guestUser.filledDetails;

    // Add a message for login message, if not handled yet and user is not logged in.
    // Also do not show 
    if (!config.loginMessageHandled && !this.isLogin && !isFilledGuestEnough) {
      this.$messageStore.addSafe(spawnSystemMessage({
        type: MessageType.DEFAULT,
        message: (loginRequired ? this.$t('chat.sys.login.required') : this.$t('chat.sys.login.optional')) as string,
      }));
    }
    
    // Switch to rule engine
    this.currentStep = stepEnum.RuleEngine;
  }

  /**
   * A message event is a message specific chat bot event. The different types
   * of messages (`MessageType.ts`) and events `SystemEvents.ts`.
   *
   * @event MessageList#message:input
   * @event FloatButton#message:input
   */
  async messageEvent ($event: BaseEvent) {
    if (this.currentStep === stepEnum['LiveChatFlow']) {
      // Check if the event is user submitted one.
      this.onUserSubmitEvent($event);
      return;
    }

    // During AI Conversation we check for allowed events separately
    if (this.currentStep === stepEnum['AiConversation']) {
      this.onUserSubmitAiConversationEvent($event);
      return;
    }

    if (this.currentStep !== stepEnum.WaitRuleEngine) {
      return;
    }

    emitArticleAutocompleteCollapse(true);
    this.$bot.submit($event);
  }

  restartChat () {
    this.$bot.restartChat();
  }

  onUserSubmitEvent (event: BaseEvent) {
    if (!EventType.isUserInputSubmittedTransactionEvent(event)) return;
    const userSubmittedEvent = (event as UserInputSubmittedTransactionEvent);
    const payload = userSubmittedEvent.payload;
    // Generate an end user message and submit.
    const message = this.t('chat.user.issue.part') + ` ${payload.issueMessages.join(', ')}`;

    // The firebase message expects a type of string.
    const type = switcher<TransactionType, string>()
      .when(TransactionType.Deposit, () => 'DEPOSITS')
      .when(TransactionType.Withdrawal, () => 'WITHDRAWALS')
      .when(TransactionType.Bet, () => 'BETS')
      .match(userSubmittedEvent.payload.type);

    // Send a message from here.
    plsInstance?.sendChatMsg({
      message,
      meta: {
        // We use this method as there are not many custom input types.
        // When the number of message types increase, we can create new types and
        // migrate this way of sending a message, to factories.
        messageKind: MessageType.USER_SUBMIT_INPUT_TRANSACTION,
        options: userSubmittedEvent.payload.selected,
        transactionKind: type,
      },
      callback: (err: any) => {
        if (err) {
          console.error('[sendChatMsg]: ', err);
        }
      },
      onComplete: (err: any) => {
        if (err) {
          console.error('[sendChatMsg]: ', err);
        }
      }
    });
  }

  onUserSubmitAiConversationEvent ($event: BaseEvent) {
    const allowedEventChecks = [EventType.isRateAiResponseEvent, EventType.isResolutionEvent];
    const isAllowedEvent = allowedEventChecks.some(check => check($event));

    if (!isAllowedEvent) return;

    this.$bot.submit($event);
  }

  showTriggerModal (trigger: MatchedChatTrigger) {
    analytics.trackPageEvent({
      name: PAGE_EVENTS.MODAL_TRIGGER_POP_UP,
      selection: trigger.chatTriggerId,
      enter: true,
    });

    const trackPageLeave = () => {
      analytics.trackPageEvent({
        name: PAGE_EVENTS.MODAL_TRIGGER_POP_UP,
        selection: trigger.chatTriggerId,
        enter: false,
      });
    };

    const triggerDialog = this.$dialog({
      content: trigger.message,
      className: 'overlay-trigger',
      buttons: [
        {
          name: this.$t('global.continue'),
          callback: () => {
            analytics.trackClick({
              name: BTN_EVTS.MODAL_TRIGGER_POP_UP_CONTINUE,
              selection: trigger.chatTriggerId,
            });

            trackPageLeave();
            trigger.report.continue();
            triggerDialog.close();
          },
        },
        {
          name: this.$t('global.end_chat'),
          class: 'primary',
          callback: async () => {
            analytics.trackClick({
              name: BTN_EVTS.MODAL_TRIGGER_POP_UP_END_CHAT,
              selection: trigger.chatTriggerId,
            });

            trackPageLeave();
            await trigger.report.end();
            triggerDialog.close();
            this.handleEndChat();
          },
        },
      ],
    });
  }

  showOpenRequestModal (trigger: MatchedChatTrigger) {
    const endChat = async () => {
      await trigger.report.end();
      triggerDialog.close();
      this.handleEndChat();
    };

    const restartChat = () => {
      this.restartChat();
      triggerDialog.close();
    };

    const goToMyRequests = () => {
      this.goToMyRequests();
      triggerDialog.close();
    };

    const messageHistory = trigger.triggerEvent && trigger.triggerMessage
      ? createMessageHistory(trigger.triggerEvent, trigger.triggerMessage)
      : [];

    const selectedOptions = {
      category: getCategoryTitleFromMessageHistory(messageHistory),
      section: getSectionTitleFromMessageHistory(messageHistory),
      article: undefined, // article does not trigger here
    };

    const triggerDialog = this.$dialog({
      componentBackground: 'transparent',
      content: <OpenRequestModal
        trigger={trigger}
        selectedOptions={selectedOptions}
        onGoToMyRequest={goToMyRequests}
        onRestartChat={restartChat}
        onEndChat={endChat}
      />,
    });
  }

  showOverlayTrigger (trigger: MatchedChatTrigger) {
    const { actionType } = trigger;

    if (actionType.id === ChatTriggerActionType.SHOW_MODAL) {
      this.showTriggerModal(trigger);
    } else if (actionType.id === ChatTriggerActionType.SHOW_OPEN_REQUEST_MODAL) {
      this.showOpenRequestModal(trigger);
    }
  }

  handleOnSubmit (msg: string) {
    this.stopTyping();
    plsInstance?.sendChatMsg({
      message: msg,
      callback: (err: any) => {
        if (err) {
          console.error('[sendChatMsg]: ', err);
          analytics.trackCriticalEvent({
            name: CRITICAL_EVENTS.CONNECT_REQUEST_MESSAGE_SEND_ERROR,
            metadata: {
              error: parseErrorMessage(err),
            },
          });
        }
      },
      onComplete: (err: any) => {
        if (err) {
          console.error('[sendChatMsg]: ', err);
        }
      }
    });
  }

  async handlePleasedChat () {
    if (!plsInstance) return;

    const params = this.connectConfig?.aiConversation ? { sessionId: analytics.sessionId } : {};
    await plsInstance?.endChat(params);

    plsInstance?.disablePleasedTypingListener();
  }

  handleEndChatLocalstorage () {
    const sessionId = this.resumeAiSessionId || analytics.sessionId;

    const existingData = localStorage.getItem(END_CHAT_FOR_RESUME_AI_SETUP);
    let sessionIds = [];

    if (existingData) {
      try {
        sessionIds = JSON.parse(existingData);
        if (!Array.isArray(sessionIds)) {
          sessionIds = []; // fallback if it's not an array
        }
      } catch (e) {
        sessionIds = [];
      }
    }

    sessionIds.push(sessionId);

    localStorage.setItem(END_CHAT_FOR_RESUME_AI_SETUP, JSON.stringify(sessionIds));
  }

  async handleEndChat () {
    this.$gtag.event(GA_EVENT_NAME_ENUM.CLOSE_CHATBOT);
    this.saveArticleMetrics({ endChat: true });

    this.updateActiveConversationList();
    this.handleEndChatLocalstorage();

    // Reset the State before Leave
    if (this.currentStep === stepEnum['LiveChatFlow'] && !this.hasSeenRate && this.agentJoined) {
      this.currentStep = stepEnum['ChatRating'];
      this.updateHasSeenRate(true);
      await this.handlePleasedChat();

      this.resetChat();
      return;
    }

    const isAiConversationActive = this.currentStep === stepEnum.AiConversation;
    // AI TODO: maybe change condition to better one, this highly relies on the values from bot event
    const isAiConversationEnded = this.currentStep === stepEnum.EndChat && this.chatRatingType === 'AI';

    if (isAiConversationActive || isAiConversationEnded || this.resumeAiSessionId) {
      // We are adding the sessionId in LS because we don't want to show the conversation again
      // when we hit refresh and call recent-conversations API which returns the object to resume the conversation
      // This is done because this end chat does not exactly end the conversation in this case
      // we only end conversation when user gives rating
      this.chatRatingType = 'AI';
      this.currentStep = stepEnum['ChatRating'];
      this.resetChat();
      return;
    }

    sendToParent('CLEAR_FLOAT');

    const endChatPromise = this.currentStep === stepEnum['LiveChatFlow'] ? this.handlePleasedChat() : Promise.resolve();
    await syncLogs({
      promises: [endChatPromise],
    });

    this.resetChat();
    reportingService.reset();
    sendToParent('CLOSE');
  };
  handleOnTyping (message: string) {
    if (!this.typing) {
      plsInstance?.sendTypingPleased(message);
    }
  }
  stopTyping () {
    if (!this.typing) return;
    plsInstance?.sendTyping(false);
    this.typing = false;
  }
  handleFileUpload (file: any, attachment: any) {
    plsInstance?.sendFile(file, (err: any) => {
      if (err) {
        console.error('err: ', err);
        const dialog = this.$dialog({
          content: 'File is not appropriate to upload',
          buttons: [{
            name: 'OK',
            callback: () => { dialog.close(); },
          }],
        });
      }
    });
  }

  async startLiveChatWithLoading (data: { description?: string; resumableChat?: null|PleasedModel.ActiveOpenChat; canBeResumed?: PleasedModel.VerifyChatOpenDto|null } = { description: '', resumableChat: null as (null|PleasedModel.ActiveOpenChat), canBeResumed: null as (null|PleasedModel.VerifyChatOpenDto) }) {
    if (this.isLiveChatLoading) return;
    this.setChannelLoading({ channel: 'chat', loading: true });
    await this.startLiveChat(data).finally(() => {
      this.setChannelLoading({ channel: 'chat', loading: false });
    });
  }

  sendMessagesToAgentChat () {
    const messages = getLiveChatQueueMessages(this.botMessages);

    if (!messages.length) {
      return Promise.resolve();
    }

    // This flow is only for live chat from "New Conversation"
    return messages.reduce((p: Promise<void>, { message, attachment, meta }) =>
      p.then(() => new Promise<void>(resolve => {
        const metadata = {
          ...meta,
          message,
        };

        analytics.track({
          name: GENERAL_EVENTS.CONNECT_REQUEST_MESSAGE_SEND,
          metadata,
        });


        plsInstance?.sendChatMsg({
          message,
          userSelection: true,
          attachment,
          meta,
          callback: (err: Error | null) => {
            analytics.track({
              name: GENERAL_EVENTS.CONNECT_REQUEST_MESSAGE_SEND_INIT_ERROR,
              metadata: { ...metadata, error: err?.message }
            });

            if (err) {
              console.error('[sendChatMsg] [Init Error]: ', err);
            }

            resolve();
          },
          onComplete: (err: Error | null) => {
            analytics.track({
              name: GENERAL_EVENTS.CONNECT_REQUEST_MESSAGE_SENT,
              metadata: { ...metadata, status: err ? 'failed' : 'success', error: err?.message }
            });

            if (err) {
              console.error('[sendChatMsg] [On Complete Error]: ', err);
            }
          }
        });
      }))
    , Promise.resolve());
  }

  async startLiveChat (data: { description?: string; resumableChat?: null|PleasedModel.ActiveOpenChat; canBeResumed?: null|PleasedModel.VerifyChatOpenDto } = { description: '', resumableChat: null as (null|PleasedModel.ActiveOpenChat), canBeResumed: null as (null|PleasedModel.VerifyChatOpenDto) }) {
    this.readyToChat(false); // reset the state
    this.ticketDescription = data.description || '';

    plsInstance = new PleasedChatClient();

    try {
      await this.postInit({ resumableChat: data.resumableChat, canBeResumed: data.canBeResumed });
      // @ts-ignore
    } catch (e: any) {
      console.error('postInit error', { err: JSON.stringify(e), isThreshold: e?.code === ErrorCode.THRESHOLD_LIMIT });
      if (e?.code === ErrorCode.THRESHOLD_LIMIT) {
        this.showThresholdLimitModal = true;

        return;
      }
    }

    this.$messageStore.add(spawnUserSystemMessage({
      message: this.t('chat.user.response.action', { action: this.t('page.connect.live_chat') }),
      id: BotConstants.IDS.USER_LIVE_CHAT,
      sendToChat: false,
    }));

    this.currentStep = stepEnum['LiveChatFlow'];
    this.chatRatingType = 'chat';

    if (this.activeConversations.selected) {
      plsInstance?.sendChatMsg({
        message: this.$t('chat.user.response.action', { action: this.$t('page.connect.live_chat') }) as string,
        callback: (err: any) => err && (console.error('[sendChatMsg]: ', err)),
        onComplete: (err: any) => err && (console.error('[sendChatMsg]: ', err))
      });
      this.activeConversations = activeConversation(this.activeConversations, { liveChatTicket: this.activeConversations.selected.id });
      this.activeConvoComments = [...this.activeConvoComments, createNotice({ message: this.$t('page.active_conversation.dont_close_window_warning') as string })];
    }

    this.sendMessagesToAgentChat();
  }

  setVisitorInfo (visitorInfo: VisitorInformation) {
    this.visitorInfo = visitorInfo;
  }
  scrollMsgListToEnd () {
    this.$refs?.msgList?.scrollToEnd();
  }

  postFeedback (config: {rating: number, comment: string, feedbackSelectionIds: number[]; type: PleasedModel.ChatRateMode }) {
    const { rating, comment, feedbackSelectionIds, type } = config;

    PleasedApi.postFeedback({
      rating,
      comment,
      feedbackSelectionIds,
      type,
      chatOrVoiceId: this.chatRatingId!,
    });
  }

  async handleFeedback (config: {rating: number, comment: string, feedbackSelectionIds: number[]; type: PleasedModel.ChatRateMode, showedConnectSupport: boolean }) {
    // For some reason, CSAT show connect support option itself, only send feedback
    if (config.showedConnectSupport) {
      this.postFeedback(config);
      return;
    }

    const dialog = this.$dialog({ content: this.$t('page.feedback.thanks') as string, className: 'thanks-class' });

    // Do not use syncLogs with waiting dialog because we'll already have "Thank you" dialog open.
    analytics.trackSessionEndEvent();
    analytics.flushLogs().then(() => {
      analytics.trackSessionEnd();
    });

    setTimeout(() => {
      dialog.close();
      sendToParent('CLEAR_FLOAT');
      sendToParent('CLOSE');
    }, 2000);

    this.postFeedback(config);
  }

  async createVoiceChatWithLoading (data: { description: string, deflectionLog?: DeflectionLog } = { description: '' }) {
    if (this.isVoiceChatLoading) return;
    this.setChannelLoading({ channel: 'voice', loading: true });
    await this.handleVoice(data).finally(() => {
      this.setChannelLoading({ channel: 'voice', loading: false });
    });
  }

  async handleVoice (data: { description: string, deflectionLog?: DeflectionLog } = { description: '' }) {
    analytics.trackVoiceEvent({ name: VOICE_EVENTS.VOICE_HANDLE });

    const { description } = data;
    const checkPermissionDialog = new Promise((resolve) => {
      if (platform === 'app') {
        listenFromParent<ParentEvDataVoicePermission>('APP_HAS_VOICE_PERMISSION', data => {
          analytics.trackVoiceEvent({
            name: VOICE_EVENTS.VOICE_MICROPHONE_PERMISSION,
            metadata: { permission: data.permission },
          });

          if (data.permission) {
            resolve(true);
          } else {
            resolve(false);
            const dialogPermison = this.$dialog({
              // TODO: [Sportybet]
              title: this.$t('page.voice.permission.title', { appName: env.get('appName') }) as string,
              // TODO: [Sportybet]
              content: this.t('page.voice.permission.reason', { appName: env.get('appName') }),
              className: 'dialog-voice-permission',
              buttons: [{
                name: this.t('page.voice.permission.not_now'),
                callback: () => {
                  analytics.trackVoiceEvent({ name: VOICE_EVENTS.VOICE_MICROPHONE_PERMISSION_DENIED });
                  dialogPermison.close();
                },
              }, {
                name: 'Ok',
                callback: () => {
                  analytics.trackVoiceEvent({ name: VOICE_EVENTS.VOICE_MICROPHONE_PERMISSION_OPEN_SETTINGS });
                  dialogPermison.close();
                  sendToParent('APP_GO_SETTING');
                },
              }],
            });
          }
        });
        sendToParent('APP_HAS_VOICE_PERMISSION');
      } else {
        resolve(true);
      }
    });

    const dialogButtons = [{
      name: this.t('page.voice.cancel'),
      callback: () => {
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.VOICE_HANDLE_CANCEL });
        dialog1.close();
      },
    }, {
      name: this.t('page.connect.call'),
      class: ['primary'],
      callback: async () => {
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.VOICE_HANDLE_CALL });

        const res = await checkPermissionDialog;
        if (!res) {
          dialog1.close();
          return;
        };
        plsInstance = new PleasedChatClient();
        dialog1.loading(true);
        await this.postInit({ isVoice: true });
        dialog1.loading(false);

        const dialog2 = this.$dialog({
          content: <Voice
            voiceChatId={this.voiceChatId}
            onCall={async (voiceComponent?: Voice) => {
              try {
                const isGuest = !this.isLogin;
                const guestObj = { // phone, email will be null in PleasedChatClient if is Geust
                  id: this.guestUser.userId,
                  phone: this.visitorInfo.phone,
                  email: this.visitorInfo.email,
                };
                const extraFieldUserSelections = getVoiceChatExtraFields(this.botMessages);

                const hadAiConversation = this.connectConfig?.aiConversation;
                const { resumeAiSessionId, isFirstRequest, overlongConversation, selectedTransactionsData } = this.connectConfig || { resumeAiSessionId: null };
                const selectedTransactionTags = selectedTransactionsData && hasSelectedTransactions(selectedTransactionsData)
                  ? generateSelectedTransactionsTags(selectedTransactionsData)
                  : [];
                const autoAddTags = [
                  this.connectConfig?.appliedMetadataAutomation?.tag,
                  isFirstRequest ? 'ai::first_api_error' : isFirstRequest === false ? 'ai::api_error' : null,
                  overlongConversation ? 'ai::exceed_msg_limit' : null,
                  // TODO: Uncomment this when we have a way to handle selected transactions in voice chat
                  //...selectedTransactionTags,
                ].filter(notEmpty);

                const { code, message, data: { voiceChatId } }: PleasedModel.ApiResponse<{voiceChatId: number;requesterId: number;twilioJWT: string;}> = await plsInstance?.requestVoice({
                  description,
                  isGuest,
                  guestObj: guestObj as any,
                  autoAddTags,
                  extraFields: [
                    ...this.extraTransactionInfo,
                    { extraFieldKey: 'query', extraFieldValue: this.ticketSubject },
                    ...extraFieldUserSelections,
                  ],
                  ticketId: this.activeConversations?.selected?.id,
                  ...(hadAiConversation ? { aiSessionId: resumeAiSessionId || analytics.sessionId } : {}),
                  chatTags: Object.entries({
                    ...this.metaTags,
                    language: this.$i18n.locale, // override the language tag.
                  }).filter(([, v]) => (v || '').trim()).map(([k, v]) => `${k}=${v.trim()}`),
                  callbacks: {
                    onReady: (device: any) => {},
                    onError: (e: any) => {
                      analytics.trackCriticalEvent({
                        name: CRITICAL_EVENTS.CONNECT_OPTIONS_CALL_INITIATED_ERROR,
                        metadata: {
                          error: parseErrorMessage(e),
                        },
                      });

                      const dialog = this.$dialog({
                        content: e.message,
                        buttons: [{
                          name: 'OK',
                          callback: () => { dialog.close(); },
                        }],
                      });
                    },
                    onConnect: (connect: any) => {
                      voiceComponent?.handleConnected();
                      this.didVoiceChatConnect = true;
                      data.deflectionLog && PleasedApi.sendDeflectionLog(data.deflectionLog);
                    },
                    onDisconnect: (conn: any) => {
                      this.checkVoiceCallFeedback();
                    },
                  },
                });
                this.voiceChatId = voiceChatId;
                if (code !== 10000) {
                  const dialog3 = this.$dialog({
                    content: message,
                    buttons: [{
                      name: 'OK',
                      callback: () => { dialog3.close(); },
                    }],
                  });
                }
                // @ts-ignore
              } catch (e: any) {
                console.error('voice error', { err: JSON.stringify(e), isThreshold: e?.code === ErrorCode.THRESHOLD_LIMIT });
                if (e?.code === ErrorCode.THRESHOLD_LIMIT) {
                  this.showThresholdLimitModal = true;
                  dialog2.close();
                  dialog1.close();
                  return;
                }

                console.error('e: ', e);
                const dialog4 = this.$dialog({
                  content: 'Something went wrong',
                  buttons: [{
                    name: 'OK',
                    callback: () => { dialog4.close(); },
                  }],
                });
              }
            }}
            onClose={() => {
              plsInstance?.hangUpVoice();
              this.checkVoiceCallFeedback();
              reportingService.reset();
            }}
            onDestroy={() => {
              plsInstance?.hangUpVoice();
            }}
            onMinimize={() => {
              console.log('onMinimize');
              const isWebPlatform = platform === 'web' || platform === 'wap';

              if (isWebPlatform) {
                this.onMaximize.push(() => {
                  dialog2.show();
                });
                dialog2.hide();
              }

              Bridge.minimize({ avoidNoop: false });
            }}
          />,
        });
      },
    }];

    // We need to fix types for SFC components which are used as JSX
    const DisclaimerComponent = VoiceDisclaimer as unknown as typeof Vue;
    const dialog1 = this.$dialog({
      content: <DisclaimerComponent buttons={dialogButtons} />,
      componentBackground: 'transparent',
    });
  }

  async checkVoiceCallFeedback () {
    if (!this.didVoiceChatConnect || !this.voiceChatId) {
      await syncLogs();
      sendToParent('CLOSE');
      return;
    }
    const loadingDialog = this.$dialog({ content: this.t('global.please_wait'), buttons: [] });
    loadingDialog.loading(true);
    const didConnect = await PleasedApi.wasVoiceChatAccepted({ voiceChatId: this.voiceChatId }).catch(() => false);
    loadingDialog.loading(false);
    loadingDialog.close();
    if (!didConnect) {
      await syncLogs();
      sendToParent('CLOSE');
      return;
    }
    this.currentStep = stepEnum['ChatRating'];
    this.chatRatingType = 'voice';
  }

  get activeConversationBack () {
    if (this.isActiveConversationSelected) {
      return this.hideActiveConversation;
    }
    if (this.isChatroomSelected) {
      return (evt: Event) => {
        evt?.preventDefault();
        this.currentStep = stepEnum.ActiveConversation;
      };
    }
    return null;
  }

  get isActiveConversationSelected () {
    return Boolean(this.activeConversations?.selected);
  }

  get isActiveConversationSelectedLiveChat () {
    return Boolean(this.activeConversations?.liveChatTicket);
  }

  hideActiveConversation (fromHistoryListener = false) {
    if (this.isActiveConversationSelectedLiveChat) {
      Bridge.minimize({ avoidNoop: true });
      return;
    }
    const { backListener } = this.activeConversations;
    backListener && window.removeEventListener('popstate', backListener as any);
    if (!fromHistoryListener) this.$router.replace('#');

    this.activeConversations = activeConversation({ ...this.activeConversations, selected: null, backListener: null, route: '' });
    this.currentStep = this.activeConversations.previousStep;
  }

  async updateActiveConversationList () {
    const { total, tickets = [] } = await PleasedApi.getActiveConversations().catch(() => ({ total: 0, tickets: [] }));

    if (!total || !tickets.length) {
      return { total, tickets };
    }

    const getComment = ({ latestComment }: PleasedModel.ActiveTicket) => {
      if (!latestComment) return '';

      const { voice, body, authorName, authorType } = latestComment;
      if (voice) {
        return `Voice Call of ${formatDuration(intervalToDuration({ start: 0, end: voice.duration * 1000 }), { format: ['minutes', 'seconds'] })}`;
      }
      const author = authorType === 'AGENT' ? authorName : 'You';
      return author ? `${author}: ${body}` : body;
    };


    this.activeConversations = activeConversation({
      ...this.activeConversations,
      total,
      conversations: tickets.map(t => ({
        id: t.id,
        title: t.subject,
        subtitle: `Ticket #: ${t.id}`,
        description: getComment(t),
        newMessages: t.commentCount,
        updatedAt: t.updatedAt,
      })),
    });

    return { total, tickets };
  }

  async populateActiveConversation () {
    const { total, tickets = [] } = await this.updateActiveConversationList();

    if (!total || !tickets.length) {
      return this.startChatBot();
    }
  }

  async resolveActiveConversation () {
    await this.populateActiveConversation();
    if (!this.activeConversations?.total || !this.activeConversations?.conversations?.length) {
      return;
    }
    this.currentStep = stepEnum.ActiveConversation;
  }

  showConversation (selected: Conversation) {
    const route = `#active-convo-${selected.id || String(Math.random()).slice(2, 10)}`;
    const backListener = (e?: PopStateEvent) => {
      this.hideActiveConversation(true);
      // The event listener is removed in another callback.
    };
    window.addEventListener('popstate', backListener);

    const isOpenRequestConversation = selected?.title?.startsWith(this.$t('chat.chat_trigger.open_request.request') as string);

    this.activeConversations = activeConversation({ ...this.activeConversations, selected, backListener, route });
    this.activeConversations.previousStep = this.currentStep;
    this.currentStep = stepEnum.RuleEngine;

    if (isOpenRequestConversation) {
      this.showLoggedInOptions.showCall = false;
      this.showLoggedInOptions.showLiveChat = false;
    } else {
      this.showLoggedInOptions.showCall = this.showLoggedInOptions.showCall && isVoiceChatSupported(platform);
    }

    // TODO: Dirty fix -
    if (this.showLoggedInOptions.showCall && env.get('country') === Country.INT && Locale.i18n.locale !== Locale.LANGUAGES.pt.code) {
      this.showLoggedInOptions.showCall = false;
    }

    this.$router.push(`/${route}`);
    this.getActiveConversationComments(selected);
    selected.id && reportingService.selectedActiveConversation(selected.id);
  }

  async getActiveConversationComments (selected: Conversation) {
    this.activeConvoComments = [createTypingMessage()];
    const comments = await PleasedApi.getActiveConversationComments(selected.id as number).then(r => r.data.comments).catch((e) => {
      console.error(e);
      return [] as PleasedModel.ActiveTicketComment[];
    });

    this.activeConvoComments = comments.flatMap(c => {
      // The logic can be improved here. We are parsing the HTML of
      // all the comments, instead we should know which comments are safe to parse from BE
      // or the parser should be moved to BE>
      const parsed = c.bodyHtml?.trim() && autoParse(c.bodyHtml);
      let messages: BaseMessage[] = [];
      if (parsed && parsed.length) {
        messages = parsed.map(m => spawnMessage({
          type: MessageType.DEFAULT,
          memberType: m.authorType === 'AGENT' ? MemberType.AGENT : MemberType.VISITOR,
          sendToChat: false,
          noSystemPrefix: true,
          message: m.message,
          timestamp: (m.timestamp?.valueOf() || Date.now() + 1),
        }));
      }
      const sanitizedBodyHtml = sanitizeHTML(c.bodyHtml);
      const message = spawnMessage({
        type: MessageType.DEFAULT,
        message: c.authorType === 'AGENT' ? sanitizedBodyHtml : c.body,
        noSystemPrefix: true,
        sendToChat: false,
        memberType: c.authorType === 'AGENT' ? MemberType.AGENT : MemberType.VISITOR,
      });
      if (c.attachments?.length) {
        const attachmentMessages = c.attachments.map(a => spawnMediaMessage({ attachment: { id: +a.id, name: a.fileName, url: a.url, mimeType: a.contentType },
          memberType: c.authorType === 'AGENT' ? MemberType.AGENT : MemberType.VISITOR,
        }));
        if (c.body === 'File Upload' || !(message?.message || '').trim() || messages.length) return [...messages, ...attachmentMessages];

        return [message, ...attachmentMessages];
      }
      return messages.length ? messages : message;
    });
  }

  removeInitialLoadingMessage () {
    const loadingMessage = this.$messageStore.get().find(x => x.id === INITIAL_LOADING_MESSAGE_ID);
    loadingMessage && this.$messageStore.remove(loadingMessage);
  }

  startChatBot () {
    this.removeInitialLoadingMessage();
    this.$bot.start();
  }

  startOrResumeNewConversation () {
    this.$gtag.event(GA_EVENT_NAME_ENUM.START_NEW_CONVERSATION);
    this.currentStep = stepEnum.WaitRuleEngine;
    this.startChatBot();
    this.$nextTick(this.scrollMsgListToEnd);
  }

  async sendActiveConversationComment (commentText: string, ticket: Conversation) {
    const typing = createTypingMessage(null, MemberType.VISITOR);
    this.activeConvoComments = [...this.activeConvoComments, typing];
    const comment = await PleasedApi.addTicketComment({ comment: commentText, ticketId: ticket.id as number });
    let activeComments = [...this.activeConvoComments];
    const loadingIdx = findMessageIndex(activeComments, typing);
    const hasLoading = loadingIdx >= 0;
    if (hasLoading) { activeComments.splice(loadingIdx, 1); }

    if (comment) {
      const noticeId = 'active-convo-follow-up-notice';
      activeComments = deleteMessage(activeComments, { id: noticeId });
      activeComments.push(spawnUserSystemMessage({ message: commentText }));
      activeComments.push(createNotice({
        id: noticeId,
        message: this.$t('notice.active_conversation.post_followup') as string,
      }));
    }

    this.activeConvoComments = activeComments;
    const activeConvo = this.activeConversations.conversations.find(convo => convo.id === ticket.id);
    if (activeConvo) {
      activeConvo.updatedAt = Date.now();
      activeConvo.description = this.$t('page.active_conversation.comment_from_user', { ticketComment: commentText }) as string;
    }

    analytics.track({
      name: `${CMP_CONTEXTS.ACTIVE_CONVERSATION}${COMPONENT_EVENTS.CONNECT_OPTIONS_FOLLOW_UP_SENT}`,
      selection: this.activeConversations.selected?.id,
    });
  }

  async sendActiveConversationFile (file: File, ticket: Conversation) {
    const typing = createTypingMessage(null, MemberType.VISITOR);
    this.activeConvoComments = [...this.activeConvoComments, typing];
    await PleasedApi.addAttachmentComment({ file, ticketId: ticket.id as number });
    const activeConvo = this.activeConversations.conversations.find(convo => convo.id === ticket.id);
    if (activeConvo) {
      activeConvo.description = this.$t('page.active_conversation.comment_from_user', { ticketComment: this.$t('page.active_conversation.comment_when_file_sent') }) as string;
    }
    this.getActiveConversationComments(ticket);
  }

  async sendCommentForAI (comment: string) {
    this.isUserFileUploadForAiEnabled = false;
    const event: UserMessageForAIEvent = {
      type: ExternalBotEvent.USER_MESSAGE_FOR_AI,
      id: randomId(),
      payload: {
        message: comment,
      },
    };

    this.$bot.submitExternalEvent(event);
  }

  async sendFileForAI (file: File) {
    this.isUserFileUploadForAiEnabled = false;

    try {
      const attachment = await PleasedApi.uploadAttachment(file);
      // send message to ai with attachment url

      const event: UserFileForAIEvent = {
        type: ExternalBotEvent.USER_FILE_FOR_AI,
        id: randomId(),
        payload: {
          fileName: attachment.fileName,
          contentType: attachment.contentType,
          fileUrl: attachment.url,
          attachmentId: attachment.id,
        },
      };

      this.$bot.submitExternalEvent(event);
    } catch (error) {
      console.error('Error in sendFileForAI', error);
      analytics.track({
        name: AI_EVENTS.AI_FILE_UPLOAD_ERROR,
        metadata: { error: parseErrorMessage(error) },
      });

      const dialog = this.$dialog({
        content: 'Error occurred while uploading file, please try again or select another file.',
        buttons: [{
          name: 'OK',
          callback: () => { dialog.close(); },
        }],
      });

      this.isUserFileUploadForAiEnabled = true;
    }
  }

  showNegativeAiFeedbackDialog (message: AiMessageForUser) {
    console.log('showNegativeAiFeedbackDialog');
    const onClose = () => {
      dialog.close();
    };

    const dialog = this.$dialog({
      content: <NegativeAiFeedbackModal message={message} onClose={onClose} />,
      componentBackground: 'transparent',
    });
  }

  /**
   * @event ActiveConversations#show:all
   */
  goToMyRequests () {
    emitOpenMyRequest(true);
  }

  submitCancelRequest (connectToAgentConfig: ConnectToAgent | null) {
    this.isStartedConnectToAgent = false;
    this.currentStep = stepEnum.WaitRuleEngine;
    const event: CancelSupportRequestExternEvent = {
      type: ExternalBotEvent.CANCEL_SUPPORT_REQUEST,
      id: randomId(),
      payload: {
        supportOptions: connectToAgentConfig,
        directRequest: this.lastDirectRequest,
      },
    };
    this.$bot.submitExternalEvent(event);

    this.connectConfig = null;
  }

  setShowThresholdLimitModal (show: boolean) {
    this.showThresholdLimitModal = show;
  }

  handleConnectFromRating () {
    this.currentStep = stepEnum.WaitRuleEngine;
    this.$bot.setAiOptions({ aiEnabledPerSession: false });
    // Currently we connect to agent after CSAT only in case of AI chat.
    // AI chat set connect config of "Source message" before starting the flow, so we have it here.
    this.connectToAgentFlow(this.connectConfig!, { category: this.connectConfig?.category || '' });
  }

  handleTransactionAiFlowInput (event: UserInputSubmittedTransactionForAiEvent) {
    this.currentStep = stepEnum.AiConversation;
    this.aiShowSelectorType = null;

    // Update connectConfig with the one from the event payload if it exists
    if (event.payload.connectConfig) {
      this.connectConfig = event.payload.connectConfig;
    }

    this.$bot.submitExternalEvent(event);
  }
}
